import { useTranslation, useUIColors } from '@/core/hooks';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Typography } from './Typography';
import { CircleAlertIcon, CircleCheckIcon, CircleIcon, CircleXIcon } from './icons';

export interface StatusChipProps {
    status: 'paid' | 'missed' | 'due-soon' | 'in-process' | 'future';
    size?: 'small' | 'medium';
    textFirst?: boolean;
}

export function StatusChip({ status, size = 'small', textFirst = false }: StatusChipProps) {
    const colors = useUIColors();
    const { t } = useTranslation();

    const getStatusConfig = () => {
        switch (status) {
            case 'paid':
                return {
                    text: t('payments.statusPaid'),
                    textColor: colors.fontSuccessText, // #388e3c
                    backgroundColor: '#c8e6c9', // greenLighter from Figma
                    Icon: CircleCheckIcon,
                };
            case 'missed':
                return {
                    text: t('propertyDetails.missed'),
                    textColor: colors.fontErrorText, // #d8181e
                    backgroundColor: '#fecdd2', // redLightest2 from Figma
                    Icon: CircleXIcon,
                };
            case 'due-soon':
                return {
                    text: t('payments.statusDueSoon'),
                    textColor: colors.fontSecondary, // #4d4d4d
                    backgroundColor: '#fef076', // yellowLighter from Figma
                    Icon: CircleAlertIcon,
                };
            case 'in-process':
                return {
                    text: t('home.inProcess'),
                    textColor: colors.fontTertiary, // #666666
                    backgroundColor: '#ededed', // Light gray background
                    Icon: CircleIcon,
                };
            case 'future':
                return {
                    text: t('propertyDetails.upcoming'),
                    textColor: '#5095EF', // bluePrimary from UI_PRIMITIVES
                    backgroundColor: '#E3F2FD', // Light blue background
                    Icon: CircleIcon,
                };
            default:
                return {
                    text: status,
                    textColor: colors.fontTertiary,
                    backgroundColor: colors.backgroundLevel2,
                    Icon: CircleAlertIcon,
                };
        }
    };

    const statusConfig = getStatusConfig();
    const isSmall = size === 'small';

    return (
        <View
            style={[
            styles.container,
            {
                backgroundColor: statusConfig.backgroundColor,
                paddingHorizontal: isSmall ? 4 : 8,
                paddingVertical: isSmall ? 4 : 6,
                },
            ]}
        >
            <View style={styles.content}>
                {textFirst ? (
                    <>
                        <Typography
                            variant={isSmall ? 'footnote' : 'subhead'}
                            style={{
                                color: statusConfig.textColor,
                                fontSize: isSmall ? (status === 'in-process' ? 12 : 13) : 14,
                                fontWeight: '400',
                            }}
                        >
                            {statusConfig.text}
                        </Typography>
                        <statusConfig.Icon
                            size={isSmall ? 12 : 14}
                            color={statusConfig.textColor}
                            {...(status === 'paid' && { strokeWidth: 6 })} // Because the CircleCheckIcon is very thin
                        />
                    </>
                ) : (
                    <>
                <statusConfig.Icon
                    size={isSmall ? 12 : 14}
                    color={statusConfig.textColor}
                    {...(status === 'paid' && { strokeWidth: 6 })} // Because the CircleCheckIcon is very thin
                />
                <Typography
                    variant={isSmall ? 'footnote' : 'subhead'}
                    style={{
                        color: statusConfig.textColor,
                        fontSize: isSmall ? (status === 'in-process' ? 12 : 13) : 14,
                                fontWeight: '400',
                    }}
                >
                    {statusConfig.text}
                </Typography>
                    </>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        borderRadius: 4,
        justifyContent: 'center',
        alignItems: 'center',
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
    },
});

export default StatusChip;